import { useState } from 'react';

import { ExceptionalPartFormData } from './ExceptionalPartCreateStep.props';
import {
  PartActionSelection,
  PartActionsStep,
  SelectedSerial,
} from './MobilePartActionsDialog.props';
import { PartConditionOptions } from '../../../../../../@types/part.type';
import {
  PartTypeActionLiveSearchItem,
  PartTypeActionLiveSearchItemCategoryCode,
} from '../../../../../../@types/partType.type';
import { ServiceObject } from '../../../../../../@types/service.type';
import { Folders } from '../../../../../../@types/uploaded-files.type';
import { UploadFileItem, uploadFiles } from '../../../../../../api/uploadedFiles';
import { ServiceAttachmentUploadFeature } from '../../../../../../configs/enums';
import { useCreateExceptionalPart } from '../../../../../../queries/part-query';

const OUT_SERIAL_ACTIONS = ['remove', 'replace', 'doa'];
const IN_SERIAL_ACTIONS = ['install', 'replace'];

const uploadPartImage = async (
  serviceId: number,
  file: File,
  isDevice: boolean
): Promise<UploadFileItem | null> => {
  const formData = new FormData();
  formData.append('fileList', file);
  try {
    const res = await uploadFiles(
      serviceId,
      formData,
      Folders.ServiceAttachments,
      ServiceAttachmentUploadFeature.PartActionPartCreation
    );
    if (res['0']?.dbSuccess) return { ...res['0'], isDeviceImage: isDevice };
  } catch {
    /* non-blocking */
  }
  return null;
};

export const buildMockItem = (
  data: ExceptionalPartFormData,
  createdPart: { id: number; serial1: string },
  conditionCode: string,
  serviceId: number
): PartTypeActionLiveSearchItem => ({
  name: data.partTypeName,
  partTypeId: data.partTypeId,
  partTypeName: data.partTypeName,
  categoryCode: PartTypeActionLiveSearchItemCategoryCode.Serialised,
  partId: createdPart.id,
  serial1: createdPart.serial1,
  conditionCode,
  softwareVersion: '',
  hardwareVersion: '',
  firmwareVersion: '',
  fleetTagName: '',
  locationName: '',
  locationId: 0,
  serviceId,
});

interface UseExceptionalPartSubmitParams {
  service: ServiceObject;
  selectedAction: PartActionSelection | null;
  makeSerial: (item: PartTypeActionLiveSearchItem) => SelectedSerial;
  setInSerial: (s: SelectedSerial) => void;
  setOutSerial: (s: SelectedSerial) => void;
  getOutSerial: () => SelectedSerial | null;
  getInSerial: () => SelectedSerial | null;
  setError: (msg: string) => void;
  setStep: (step: PartActionsStep) => void;
  executeSubmit: (
    inSerial: SelectedSerial | null,
    outSerial: SelectedSerial | null,
    isExceptional: boolean
  ) => Promise<void>;
}

export const useExceptionalPartSubmit = ({
  service,
  selectedAction,
  makeSerial,
  setInSerial,
  setOutSerial,
  getOutSerial,
  getInSerial,
  setError,
  setStep,
  executeSubmit,
}: UseExceptionalPartSubmitParams) => {
  const [exceptionalFor, setExceptionalFor] = useState<'in' | 'out' | null>(null);
  const [exceptionalInitialSerial, setExceptionalInitialSerial] = useState('');
  const [canSubmitExceptional, setCanSubmitExceptional] = useState(false);
  const [isInSerialExceptional, setIsInSerialExceptional] = useState(false);
  const [isOutSerialExceptional, setIsOutSerialExceptional] = useState(false);

  const createExceptionalPartMutation = useCreateExceptionalPart();

  const handleExceptionalSubmit = async (data: ExceptionalPartFormData): Promise<void> => {
    if (!selectedAction || !exceptionalFor) return;
    setError('');
    const imageData: UploadFileItem[] = [];
    if (data.deviceImageFile) {
      const u = await uploadPartImage(service.id, data.deviceImageFile, true);
      if (u) imageData.push(u);
    }
    if (data.labelImageFile) {
      const u = await uploadPartImage(service.id, data.labelImageFile, false);
      if (u) imageData.push(u);
    }
    const conditionCode =
      exceptionalFor === 'in' ? PartConditionOptions.GOOD : PartConditionOptions.FAULTY;
    try {
      const createdPart = (await createExceptionalPartMutation.mutateAsync({
        partTypeId: data.partTypeId,
        serial1: data.serial1,
        conditionCode,
        serviceId: service.id,
        exception: true,
        isAccepted: false,
        partActionType: selectedAction.code,
        imageData,
        isActive: false,
      })) as { id: number; serial1: string };
      const selectedSerial = makeSerial(
        buildMockItem(data, createdPart, conditionCode, service.id)
      );
      const currentFor = exceptionalFor;
      setExceptionalFor(null);
      if (currentFor === 'in') {
        setInSerial(selectedSerial);
        setIsInSerialExceptional(true);
        await executeSubmit(selectedSerial, getOutSerial(), true);
      } else {
        setOutSerial(selectedSerial);
        setIsOutSerialExceptional(true);
        await executeSubmit(getInSerial(), selectedSerial, true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create part');
    }
  };

  const resetExceptional = (): void => {
    setExceptionalFor(null);
    setExceptionalInitialSerial('');
    setCanSubmitExceptional(false);
    setIsInSerialExceptional(false);
    setIsOutSerialExceptional(false);
  };

  const onCreateOutExceptionally =
    selectedAction && OUT_SERIAL_ACTIONS.includes(selectedAction.code)
      ? (serial: string) => {
          setExceptionalFor('out');
          setExceptionalInitialSerial(serial);
          setStep('exceptional-create');
        }
      : undefined;

  const onCreateInExceptionally =
    selectedAction && IN_SERIAL_ACTIONS.includes(selectedAction.code)
      ? (serial: string) => {
          setExceptionalFor('in');
          setExceptionalInitialSerial(serial);
          setStep('exceptional-create');
        }
      : undefined;

  return {
    exceptionalFor,
    setExceptionalFor,
    exceptionalInitialSerial,
    canSubmitExceptional,
    setCanSubmitExceptional,
    isInSerialExceptional,
    isOutSerialExceptional,
    createExceptionalPartMutation,
    handleExceptionalSubmit,
    resetExceptional,
    onCreateOutExceptionally,
    onCreateInExceptionally,
  };
};
